import isEqual from 'lodash.isequal';
import { FC, MouseEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { ReactComponent as Dots } from '@/assets/icons/dots.svg';
import { ReactComponent as Eye } from '@/assets/icons/eye.svg';
import { ReactComponent as Reset } from '@/assets/icons/reset.svg';
import { ReactComponent as ViewOptionsChangedIcon } from '@/assets/icons/view-options-changed.svg';
import { ReactComponent as ViewOptionsIcon } from '@/assets/icons/view-options.svg';
import { ReactComponent as X } from '@/assets/icons/x.svg';
import Button from '@/components/Button';
import { Box, Footer, Header, Inner, Item, Title } from '@/components/Dropdown';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Dispatch, RootState } from '@/utils/store';

const StyledText = styled(Text)`
  transition: color ease-out 0.2s;
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const StyledButton = styled(Button)<{ $noSvgMargin: boolean }>`
  > svg {
    flex-shrink: 0;

    ${({ $noSvgMargin }) =>
      $noSvgMargin &&
      css`
        margin: 0 !important;
      `}
  }
`;

const StyledViewOptionsChangedIcon = styled(ViewOptionsChangedIcon)`
  margin-top: -2px;
  margin-right: 6px !important;
`;

export const OptionsHolder = styled.div`
  position: relative;
`;

const StyledBox = styled(Box)`
  width: 280px;
`;

interface ToggableColumn {
  title: string;
  toggable: boolean;
  defaultVisibility: boolean;
}

interface Props {
  columns: Array<ToggableColumn>;
  tableKey: string;
  className?: string;
  hideButtonText?: boolean;
}

const ViewOptionsModal: FC<Props> = ({
  tableKey,
  columns,
  className,
  hideButtonText = false,
}) => {
  const [showViewOptions, setShowViewOptions] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowViewOptions(false));

  const dispatch = useDispatch<Dispatch>();

  const defaultTableViewOptions = columns
    .filter((column) => column.toggable)
    .reduce((finalObject, column) => {
      return { ...finalObject, [column.title]: column.defaultVisibility };
    }, {});

  const tableViewOptions =
    useSelector(
      (rootState: RootState) => rootState.options.tableViews[tableKey],
    ) ?? {};

  const alwaysVisibleColumns = columns.filter((column) => {
    return !column.toggable;
  });

  const visibleColumns = columns.filter((column) => {
    if (!column.toggable) {
      return false;
    }

    if (tableViewOptions[column.title] === undefined) {
      return column.defaultVisibility;
    }

    return tableViewOptions[column.title];
  });

  const hiddenColumns = columns.filter((column) => {
    if (tableViewOptions[column.title] === undefined) {
      return !column.defaultVisibility;
    }

    return !tableViewOptions[column.title];
  });

  const toggleColumns = (name: string) => {
    const isColumnVisible =
      tableViewOptions[name] !== undefined
        ? !tableViewOptions[name]
        : !(
            columns.find((col) => col.title === name)?.defaultVisibility ??
            false
          );

    const newOptions = { ...tableViewOptions, [name]: isColumnVisible };

    dispatch.options.setTableViewsOptions({
      tableKey,
      newOptions,
    });
  };

  const showAllColumns = () => {
    dispatch.options.setTableViewsOptions({
      tableKey,
      newOptions: columns
        .filter((column) => column.toggable)
        .reduce((finalObject, column) => {
          return { ...finalObject, [column.title]: true };
        }, {}),
    });
  };

  const resetColumns = () => {
    dispatch.options.setTableViewsOptions({
      tableKey,
      newOptions: defaultTableViewOptions,
    });
  };

  return (
    <OptionsHolder ref={ref}>
      <StyledButton
        onClick={() => setShowViewOptions(!showViewOptions)}
        className={className}
        small
        color="action"
        $noSvgMargin={hideButtonText}
        active={
          !!showViewOptions ||
          !isEqual(tableViewOptions, defaultTableViewOptions)
        }
      >
        {isEqual(tableViewOptions, defaultTableViewOptions) ? (
          <ViewOptionsIcon />
        ) : (
          <StyledViewOptionsChangedIcon />
        )}
        {!hideButtonText && 'View options'}
      </StyledButton>
      {showViewOptions && (
        <StyledBox>
          <Header>
            <Text $color="black" $type="smallBold">
              View options
            </Text>
            <button onClick={() => setShowViewOptions(!showViewOptions)}>
              <X />
            </button>
          </Header>
          <Inner>
            <Title $color="gray" $type="smallBold">
              Shown in table
            </Title>
            {alwaysVisibleColumns.map((column) => (
              <Item key={column.title} $disabled $hidden>
                <div>
                  <Text $color="black" $type="smallBold">
                    <Dots />
                    {column.title}
                  </Text>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 2.5L13 13.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.68155 9.85012C9.19092 10.2961 8.54325 10.5289 7.88099 10.4973C7.21874 10.4657 6.59614 10.1724 6.15016 9.6818C5.70417 9.19121 5.47132 8.54356 5.50283 7.8813C5.53433 7.21904 5.82762 6.59642 6.31816 6.15039"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.62465 4.28711C2.07657 5.57746 1 8.00001 1 8.00001C1 8.00001 3 12.4995 8 12.4995C9.1715 12.5089 10.3284 12.239 11.3748 11.7124"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.0383 10.5692C14.4009 9.34876 15.0002 8.0005 15.0002 8.0005C15.0002 8.0005 13.0002 3.50001 8.00016 3.50001C7.56711 3.4993 7.13475 3.53451 6.70752 3.60527"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.47021 5.54395C9.00157 5.64598 9.48553 5.91754 9.84948 6.3179C10.2134 6.71827 10.4378 7.22584 10.4888 7.76449"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Item>
            ))}
            {visibleColumns.map((column) => (
              <Item
                key={column.title}
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  toggleColumns(column.title);
                }}
              >
                <div>
                  <Text $color="black" $type="smallBold">
                    <Dots />
                    {column.title}
                  </Text>
                </div>
                <Eye />
              </Item>
            ))}
            {hiddenColumns.length > 0 && (
              <>
                <Title $color="gray" $type="smallBold">
                  Hidden in table
                  <StyledText
                    as="button"
                    $color="gray"
                    $type="extraSmall"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      showAllColumns();
                    }}
                  >
                    Show all
                  </StyledText>
                </Title>
                {hiddenColumns.map((column) => (
                  <Item
                    $hidden
                    key={column.title}
                    as="button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      toggleColumns(column.title);
                    }}
                  >
                    <div>
                      <Text $color="black" $type="smallBold">
                        <Dots />
                        {column.title}
                      </Text>
                    </div>
                    <svg
                      width="14"
                      height="8"
                      viewBox="0 0 14 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M1.58363 0.778704C1.32333 0.456501 0.851115 0.406321 0.528912 0.666625C0.206709 0.926929 0.15653 1.39914 0.416833 1.72135C0.701343 2.07351 1.04801 2.45202 1.46038 2.82145L0.34377 4.75547C0.136663 5.11419 0.25957 5.57288 0.618289 5.77999C0.977009 5.98709 1.4357 5.86419 1.64281 5.50547L2.66822 3.7294C3.20599 4.06354 3.81514 4.35671 4.49942 4.57142L4.17485 6.41218C4.10292 6.8201 4.3753 7.2091 4.78322 7.28102C5.19114 7.35295 5.58013 7.08057 5.65206 6.67265L5.96729 4.88489C6.2978 4.92426 6.64203 4.94533 7.00027 4.94533C7.35673 4.94533 7.69933 4.92446 8.02834 4.88548L8.34339 6.67224C8.41532 7.08016 8.80431 7.35254 9.21223 7.28061C9.62016 7.20868 9.89253 6.81969 9.8206 6.41177L9.49636 4.57291C10.181 4.35857 10.7905 4.06567 11.3286 3.73172L12.3475 5.49659C12.5546 5.85531 13.0133 5.97821 13.372 5.77111C13.7308 5.564 13.8537 5.10531 13.6465 4.74659L12.5368 2.82445C12.9506 2.45407 13.2984 2.07448 13.5837 1.72136C13.844 1.39916 13.7938 0.926945 13.4716 0.66664C13.1494 0.406336 12.6772 0.456514 12.4169 0.778717C12.0836 1.19131 11.6596 1.6359 11.1361 2.04155C11.1208 2.05244 11.106 2.06382 11.0917 2.07567C10.4127 2.59333 9.56933 3.04358 8.54276 3.27649C8.53091 3.278 8.51904 3.27981 8.50715 3.2819C8.47563 3.28746 8.44492 3.29491 8.41511 3.30412C7.97657 3.39453 7.50543 3.44533 7.00027 3.44533C6.48888 3.44533 6.01235 3.39327 5.56921 3.30076C5.54293 3.29308 5.51596 3.28677 5.48837 3.2819C5.48071 3.28055 5.47306 3.27932 5.46542 3.27821C4.4367 3.04611 3.59169 2.59577 2.91145 2.07766C2.89361 2.06253 2.87492 2.04813 2.85541 2.03453C2.33605 1.63077 1.91504 1.18892 1.58363 0.778704Z"
                        fill="currentColor"
                      />
                    </svg>
                  </Item>
                ))}
              </>
            )}
          </Inner>
          <Footer
            as="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              resetColumns();
            }}
          >
            <Reset />
            <Text $color="black" $type="smallBold">
              Reset properties
            </Text>
          </Footer>
        </StyledBox>
      )}
    </OptionsHolder>
  );
};

export default ViewOptionsModal;
